import React, { useCallback, useEffect, useState, useRef } from 'react';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { useFetchAggregatedShipToPartiesForTxa } from '@css-xp/ppc-api';
import {
  AggregatedShipToPartyOptions,
  CommerceAddress,
  PartnerSearchOptions,
  PartnerSearchContext,
} from '@css-xp/ppc-graphtypes';
import { CUSTOMERS_PAGE_UTILS } from '@css-xp/ppc-utils';
import { emptyStateProp } from '@css-xp/ppc-common';
import { SoldToAddressTableV2 } from './sold-to-address-table-v2';
import { CustomerListTable } from './customer-list-table';

interface AggregatedSoldToAddressProps {
  txaId: string;
  billToAddress?: CommerceAddress | null;
  hidePricingEligibility?: boolean;
  showAddAddress?: boolean;
  hideActions?: boolean;
  showSearchBox?: boolean;
  searchTerm?: string;
  hideEdit?: boolean;
  hideAddFromBillToAddress?: boolean;
  partnerSearchContext?: string;
  showAddAddressInRow?: boolean;
  emptyState?: any;
  heading?: React.ReactNode;
  updateCsvSearchOptions?: (value: any) => void;
  setIsExportCsvButtonDisabled?: (value: boolean) => void;
  isOrgSearchEnabled?: boolean;
  shouldRenderNewTable?: boolean;
  downloadCustomersListCSV?: () => void;
  customersListLoading?: boolean;
  isSoldToAddressArchivalEnabled?: boolean;
  isDuplicateCustomerNameEnabled?: boolean;
}

export const AggregatedSoldToAddress = ({
  txaId,
  billToAddress,
  hidePricingEligibility,
  showAddAddress,
  hideActions,
  showSearchBox,
  searchTerm = '',
  hideEdit,
  hideAddFromBillToAddress,
  partnerSearchContext,
  showAddAddressInRow = true,
  emptyState = emptyStateProp,
  heading,
  updateCsvSearchOptions,
  setIsExportCsvButtonDisabled,
  isOrgSearchEnabled,
  shouldRenderNewTable,
  downloadCustomersListCSV,
  customersListLoading,
  isSoldToAddressArchivalEnabled,
  isDuplicateCustomerNameEnabled,
}: AggregatedSoldToAddressProps) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const aggregatedOptionsRef = useRef<AggregatedShipToPartyOptions>(
    CUSTOMERS_PAGE_UTILS.buildAggregatedOptions(searchTerm, partnerSearchContext)
  );
  useEffect(() => {
    aggregatedOptionsRef.current = CUSTOMERS_PAGE_UTILS.buildAggregatedOptions(searchTerm, partnerSearchContext);
  }, [partnerSearchContext]);

  const { data, loading, refetch, error } = useFetchAggregatedShipToPartiesForTxa({
    txaId,
    aggregatedShipToPartyOptions: aggregatedOptionsRef.current,
    isOrgSearchEnabled,
  });

  const onPageChange = useCallback(
    (newPage: number) => {
      const offset = (newPage - 1) * rowsPerPage;

      const newAggregatedShipToPartyOptions = {
        ...aggregatedOptionsRef.current,
        pageOptions: {
          cursor: String(offset),
          size: rowsPerPage,
        },
      };
      aggregatedOptionsRef.current = newAggregatedShipToPartyOptions;
      setCurrentPage(newPage);
      refetch({
        aggregatedShipToPartyOptions: newAggregatedShipToPartyOptions,
      });
    },
    [rowsPerPage, currentPage, data]
  );

  const handleRowChange = useCallback(
    (newRowPerPage: number) => {
      if (newRowPerPage === rowsPerPage) {
        return;
      }
      const newAggregatedShipToPartyOptions = {
        ...aggregatedOptionsRef.current,
        pageOptions: {
          cursor: null,
          size: newRowPerPage,
        },
      };
      aggregatedOptionsRef.current = newAggregatedShipToPartyOptions;
      setRowsPerPage(newRowPerPage);
      setCurrentPage(1);
      refetch({
        aggregatedShipToPartyOptions: newAggregatedShipToPartyOptions,
      });
    },
    [rowsPerPage, currentPage]
  );

  const onSearch = useCallback(
    (searchTerm: string) => {
      const newAggregatedShipToPartyOptions = {
        ...aggregatedOptionsRef.current,
        searchOptions: {
          query: searchTerm,
          searchContext: PartnerSearchContext.ALL,
        },
        pageOptions: {
          cursor: null,
          size: rowsPerPage,
        },
      };
      aggregatedOptionsRef.current = newAggregatedShipToPartyOptions;
      setCurrentPage(DEFAULT_PAGE);
      updateCsvSearchOptions?.(newAggregatedShipToPartyOptions?.searchOptions);
      refetch({
        aggregatedShipToPartyOptions: newAggregatedShipToPartyOptions,
      });
    },
    [rowsPerPage]
  );

  const response = data?.transactionAccount.aggregatedShipToParty;
  const responseTotalCount = response?.totalCount;
  setIsExportCsvButtonDisabled?.(!responseTotalCount);

  if (shouldRenderNewTable) {
    return (
      <CustomerListTable
        txaId={txaId}
        addressResponse={response}
        customerListLoading={loading}
        error={error}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onRowChange={handleRowChange}
        onPageChange={onPageChange}
        downloadCustomersListCSV={downloadCustomersListCSV}
        customersListLoading={customersListLoading}
        onSearch={onSearch}
        refetch={refetch}
        isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
        isDuplicateCustomerNameEnabled={isDuplicateCustomerNameEnabled}
      />
    );
  }

  return (
    <div data-testid="transaction-account-ship-to-address">
      <SoldToAddressTableV2
        heading={heading}
        error={error}
        searchTerm={searchTerm}
        addressResponse={response}
        tableLoading={loading}
        transactionAccountId={txaId}
        billToAddress={billToAddress}
        onSearch={onSearch}
        hidePricingEligibility={hidePricingEligibility}
        showAddAddress={showAddAddress}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onRowChange={handleRowChange}
        onPageChange={onPageChange}
        hideActions={hideActions}
        showSearchBox={showSearchBox}
        tableRefetch={refetch}
        hideEdit={hideEdit}
        hideAddFromBillToAddress={hideAddFromBillToAddress}
        showAddAddressInRow={showAddAddressInRow}
        searchOptions={aggregatedOptionsRef.current.searchOptions as PartnerSearchOptions}
        emptyState={emptyState}
        isOrgSearchEnabled={isOrgSearchEnabled}
      />
    </div>
  );
};
